import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Divider,
  FormControl,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { BRAND, BRAND_RADIUS, brandAlpha } from '../../theme/brandTokens';
import {
  Add as AddIcon,
  ChevronRight as ChevronRightIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ManagementPageLayout, { ManagementPageCard } from '../../components/layout/ManagementPageLayout';
import ManagementPageSkeleton from '../../components/layout/ManagementPageSkeleton';
import PrimaryActionButton from '../../components/common/PrimaryActionButton';
import CreateStoreCustomerDialog from '../../components/customers/CreateStoreCustomerDialog';
import ImportCustomersFromFileDialog from '../../components/customers/import/ImportCustomersFromFileDialog';
import { useToast } from '../../context/ToastContext';
import usePermissions from '../../hooks/usePermissions';
import { Permissions } from '../../constants/Permissions';
import StoreCustomerService, {
  DEFAULT_ITEMS_PER_PAGE,
  ITEMS_PER_PAGE_OPTIONS,
} from '../../services/storeCustomerService';
import { formatCalendarDate, formatUtcDate } from '../../utils/dateFormatUtils';
import { buildTextFieldSx, FIELD_PLACEHOLDER_COLOR } from '../../components/common/formFieldStyles';

const SEARCH_DEBOUNCE_MS = 300;

const getGenreLabelKey = (genre) => {
  if (!genre) {
    return null;
  }

  return genre.toString().toLowerCase();
};

const CustomerDetailRow = ({ label, value }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: 2,
      py: 0.75,
    }}
  >
    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Box>
);

const StoreCustomersPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { storeId } = useParams();
  const navigate = useNavigate();
  const { showSuccess } = useToast();
  const { hasPermission } = usePermissions(storeId);

  const [customers, setCustomers] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const canCreate = hasPermission(Permissions.CustomersCreate);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  const loadCustomers = useCallback(async (
    nextPageIndex = pageIndex,
    nextItemsPerPage = itemsPerPage,
    nextSearch = debouncedSearch,
  ) => {
    if (!storeId) {
      setError(t('customersManagement.error.noStoreSelected'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await StoreCustomerService.getStoreCustomers(storeId, {
        pageIndex: nextPageIndex,
        itemsPerPage: nextItemsPerPage,
        search: nextSearch,
      });
      setCustomers(result.data);
      setTotalItems(result.totalItems);
      setPageIndex(result.pageIndex);
      setItemsPerPage(result.itemsPerPage || nextItemsPerPage);
    } catch (loadError) {
      console.error('Failed to load store customers:', loadError);
      setError(loadError?.response?.data?.message || t('customersManagement.error.loading'));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, itemsPerPage, pageIndex, storeId, t]);

  useEffect(() => {
    setSearchInput('');
    setDebouncedSearch('');
  }, [storeId]);

  useEffect(() => {
    loadCustomers(0, itemsPerPage, debouncedSearch);
    // Initial load / store or search change — page/size changes go through handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, debouncedSearch]);

  const pageCount = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const rangeFrom = totalItems === 0 ? 0 : pageIndex * itemsPerPage + 1;
  const rangeTo = Math.min(totalItems, (pageIndex + 1) * itemsPerPage);

  const handlePageChange = (_, newPage) => {
    loadCustomers(newPage, itemsPerPage, debouncedSearch);
  };

  const handleItemsPerPageChange = (event) => {
    const nextItemsPerPage = Number(event.target.value);
    setItemsPerPage(nextItemsPerPage);
    loadCustomers(0, nextItemsPerPage, debouncedSearch);
  };

  const handleCreated = (_createdCustomer, { assigned, hasEmail } = {}) => {
    const successKey = assigned
      ? 'customersManagement.create.success.assigned'
      : hasEmail
        ? 'customersManagement.create.success.created'
        : 'customersManagement.create.success.createdPhoneOnly';
    showSuccess(t(successKey));
    loadCustomers(0, itemsPerPage, debouncedSearch);
  };

  const handleCloseCreate = () => {
    setCreateOpen(false);
  };

  const handleImported = (result) => {
    showSuccess(t('customersManagement.import.success', {
      created: result?.createdCount ?? 0,
      assigned: result?.assignedCount ?? 0,
      alreadyInStore: result?.alreadyInStoreCount ?? 0,
    }));
    setImportOpen(false);
    loadCustomers(0, itemsPerPage, debouncedSearch);
  };

  const openCustomer = (customerId) => {
    navigate(`/management/stores/${storeId}/customers/${customerId}`);
  };

  const handleCustomerKeyDown = (event, customerId) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openCustomer(customerId);
    }
  };

  const formatGenre = (genre) => {
    const genreKey = getGenreLabelKey(genre);
    if (!genreKey) {
      return t('common.notProvided');
    }
    return t(`customersManagement.genre.${genreKey}`, { defaultValue: genre });
  };

  if (loading && customers.length === 0 && !error && !debouncedSearch) {
    return (
      <ManagementPageLayout
        pageTitle={t('customersManagement.title')}
        pageSubtitle={t('customersManagement.subtitle')}
      >
        <ManagementPageSkeleton />
      </ManagementPageLayout>
    );
  }

  return (
    <ManagementPageLayout
      pageTitle={t('customersManagement.title')}
      pageSubtitle={t('customersManagement.subtitle')}
    >
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <ManagementPageCard>
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'stretch', md: 'center' },
              justifyContent: { xs: 'stretch', md: 'flex-end' },
              gap: 1.5,
              mb: { xs: 2.5, md: 4 },
              pt: { xs: 0.5, md: 1 },
            }}
          >
            <TextField
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={t('customersManagement.dropdown.searchPlaceholder')}
              fullWidth
              inputProps={{ 'aria-label': t('customersManagement.dropdown.searchPlaceholder') }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: FIELD_PLACEHOLDER_COLOR }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                ...buildTextFieldSx(),
                order: { xs: 3, md: 1 },
                flex: { md: 1 },
                minWidth: 0,
                '& .MuiInputBase-input::placeholder': {
                  color: FIELD_PLACEHOLDER_COLOR,
                  opacity: 1,
                },
              }}
            />
            {canCreate ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => setImportOpen(true)}
                  fullWidth={isMobile}
                  sx={{
                    order: { xs: 1, md: 2 },
                    borderColor: BRAND.accent,
                    color: BRAND.accentText,
                    textTransform: 'none',
                    fontWeight: 700,
                    minHeight: 48,
                    px: { xs: 2, sm: 3 },
                    borderRadius: BRAND_RADIUS.button,
                    flexShrink: 0,
                    '&:hover': {
                      borderColor: BRAND.accentDark,
                      backgroundColor: brandAlpha('accent', 0.08),
                    },
                  }}
                >
                  {t('customersManagement.import.action')}
                </Button>
                <PrimaryActionButton
                  startIcon={<AddIcon />}
                  onClick={() => setCreateOpen(true)}
                  fullWidth={isMobile}
                  sx={{
                    order: { xs: 2, md: 3 },
                    px: { xs: 2, sm: 4 },
                    py: 1.5,
                    flexShrink: 0,
                  }}
                >
                  {t('customersManagement.create.action')}
                </PrimaryActionButton>
              </>
            ) : null}
          </Box>

          {customers.length === 0 && !loading ? (
            <Box sx={{ textAlign: 'center', py: { xs: 4, md: 6 } }}>
              <PersonIcon sx={{ fontSize: 48, color: BRAND.accentText, mb: 1.5 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                {t(debouncedSearch
                  ? 'customersManagement.empty.noResultsTitle'
                  : 'customersManagement.empty.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(debouncedSearch
                  ? 'customersManagement.empty.noResultsSubtitle'
                  : 'customersManagement.empty.subtitle')}
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5, opacity: loading ? 0.6 : 1 }}>
                {customers.map((customer) => (
                  <Box
                    key={customer.customerId}
                    role="button"
                    tabIndex={0}
                    onClick={() => openCustomer(customer.customerId)}
                    onKeyDown={(event) => handleCustomerKeyDown(event, customer.customerId)}
                    sx={{
                      border: `1px solid ${BRAND.line}`,
                      borderRadius: 2,
                      p: 2,
                      backgroundColor: BRAND.surface,
                      cursor: 'pointer',
                      minHeight: 48,
                      transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
                      '&:hover': {
                        borderColor: BRAND.accent,
                        boxShadow: 'none',
                      },
                      '&:active': {
                        backgroundColor: brandAlpha('accent', 0.08),
                      },
                      '&:focus-visible': {
                        outline: `2px solid ${BRAND.accent}`,
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                      <Avatar src={customer.picture || undefined} sx={{ width: 40, height: 40 }}>
                        {!customer.picture ? customer.name?.charAt(0)?.toUpperCase() : null}
                      </Avatar>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3, flex: 1, minWidth: 0 }}>
                        {customer.name}
                      </Typography>
                      <ChevronRightIcon sx={{ color: BRAND.accentText, flexShrink: 0 }} />
                    </Box>
                    <Divider sx={{ mb: 0.5 }} />
                    <CustomerDetailRow
                      label={t('customersManagement.table.email')}
                      value={customer.email || t('common.notProvided')}
                    />
                    <CustomerDetailRow
                      label={t('customersManagement.table.phone')}
                      value={customer.phone || t('common.notProvided')}
                    />
                    <CustomerDetailRow
                      label={t('customersManagement.table.genre')}
                      value={formatGenre(customer.genre)}
                    />
                    <CustomerDetailRow
                      label={t('customersManagement.table.birthDate')}
                      value={customer.birthDate ? formatCalendarDate(customer.birthDate) : t('common.notProvided')}
                    />
                    <CustomerDetailRow
                      label={t('customersManagement.table.addedOn')}
                      value={formatUtcDate(customer.createdAtUtc)}
                    />
                  </Box>
                ))}
              </Box>

              <TableContainer sx={{ display: { xs: 'none', md: 'block' }, opacity: loading ? 0.6 : 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('customersManagement.table.customer')}</TableCell>
                      <TableCell>{t('customersManagement.table.email')}</TableCell>
                      <TableCell>{t('customersManagement.table.phone')}</TableCell>
                      <TableCell>{t('customersManagement.table.genre')}</TableCell>
                      <TableCell>{t('customersManagement.table.birthDate')}</TableCell>
                      <TableCell>{t('customersManagement.table.addedOn')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customers.map((customer) => (
                      <TableRow
                        key={customer.customerId}
                        hover
                        tabIndex={0}
                        onClick={() => openCustomer(customer.customerId)}
                        onKeyDown={(event) => handleCustomerKeyDown(event, customer.customerId)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: brandAlpha('accent', 0.08),
                            boxShadow: `inset 0 0 0 2px ${BRAND.accent}`,
                          },
                          '&:focus-visible': {
                            outline: `2px solid ${BRAND.accent}`,
                            outlineOffset: -2,
                          },
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar src={customer.picture || undefined} sx={{ width: 36, height: 36 }}>
                              {!customer.picture ? customer.name?.charAt(0)?.toUpperCase() : null}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {customer.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{customer.email || t('common.notProvided')}</TableCell>
                        <TableCell>{customer.phone || t('common.notProvided')}</TableCell>
                        <TableCell>{formatGenre(customer.genre)}</TableCell>
                        <TableCell>
                          {customer.birthDate ? formatCalendarDate(customer.birthDate) : t('common.notProvided')}
                        </TableCell>
                        <TableCell>{formatUtcDate(customer.createdAtUtc)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: { xs: 'center', sm: 'space-between' },
                  gap: 1.5,
                  borderTop: `1px solid ${BRAND.line}`,
                  mt: 2,
                  pt: 1.5,
                  px: { xs: 0, sm: 1 },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: { xs: 1.5, sm: 2 },
                    order: { xs: 2, sm: 1 },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: BRAND.text, whiteSpace: 'nowrap' }}>
                      {t('common.rowsPerPage')}
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 72 }}>
                      <Select
                        value={itemsPerPage}
                        onChange={handleItemsPerPageChange}
                        disabled={loading}
                        MenuProps={{ disableScrollLock: true }}
                        sx={{
                          height: 36,
                          fontWeight: 600,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: BRAND.line,
                            borderWidth: '2px',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: BRAND.accent,
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: BRAND.accent,
                          },
                        }}
                      >
                        {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: BRAND.text }}>
                    {`${rangeFrom}-${rangeTo} ${t('common.of')} ${totalItems}`}
                  </Typography>
                </Box>
                <Pagination
                  count={pageCount}
                  page={pageIndex + 1}
                  onChange={(_, page) => handlePageChange(null, page - 1)}
                  disabled={loading}
                  showFirstButton={!isMobile}
                  showLastButton={!isMobile}
                  siblingCount={isMobile ? 0 : 1}
                  boundaryCount={1}
                  size={isMobile ? 'small' : 'medium'}
                  color="primary"
                  sx={{
                    order: { xs: 1, sm: 2 },
                    '& .MuiPaginationItem-root': {
                      fontWeight: 600,
                    },
                    '& .Mui-selected': {
                      backgroundColor: `${BRAND.accent} !important`,
                      color: BRAND.onAccent,
                    },
                  }}
                />
              </Box>
            </>
          )}
        </Box>
      </ManagementPageCard>

      {canCreate ? (
        <>
          <CreateStoreCustomerDialog
            open={createOpen}
            onClose={handleCloseCreate}
            storeId={storeId}
            onCreated={handleCreated}
          />
          <ImportCustomersFromFileDialog
            open={importOpen}
            onClose={() => setImportOpen(false)}
            storeId={storeId}
            onImported={handleImported}
          />
        </>
      ) : null}
    </ManagementPageLayout>
  );
};

export default StoreCustomersPage;
